/**
 * Morning Boot — What's Next's first screen (roadmap S-16).
 *
 * Five steps, worded by the Morning Boot package (basic mode): purge, tag,
 * rank the top three, timebox the North Star, focus. Every step writes the
 * shared record (morningBoot.ts says which object each one uses), and the
 * screen reads today's ranking and timebox back from it, so a reload lands on
 * Focus with the day's three.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CalendarPlus } from 'lucide-react';
import {
  TAG_LABELS,
  buildNorthStarTimeboxEvent,
  defaultTimeboxStart,
  generateGoogleCalendarLink,
  getStepsForMode,
  type BootTag,
} from '@priorities-ai/morning-boot';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useCommitmentStore } from '@/stores/prioritization/commitmentStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import type { Item } from '@/types/prioritization/lifecycle';
import { getAppDefinition } from '../registry';
import { Button } from '@/components/ui/Button';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import { findKeptRanking, rankAlone, type KeptRanking } from '../personalRanking';
import {
  WHATS_NEXT_APP_ID,
  bootTagsOf,
  localIsoDate,
  northStarOf,
  plateItems,
  rankingCandidates,
  startOfDay,
  timeboxDescription,
  timeboxTitle,
  todaysTimebox,
  withBootTag,
} from './morningBoot';

const STEPS = getStepsForMode('basic');
/** Short names for the step pills; the package's titles are full sentences. */
const STEP_LABELS = ['Purge', 'Tag', 'Rank', 'Timebox', 'Focus'];
const BOOT_TAGS: BootTag[] = ['A', 'T', 'G'];

function toTimeInput(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function fromTimeInput(value: string): Date {
  const [h, m] = value.split(':').map(Number);
  const at = new Date();
  at.setHours(h || 0, m || 0, 0, 0);
  return at;
}

export default function MorningBootScreen() {
  const app = getAppDefinition(WHATS_NEXT_APP_ID)!;
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const items = useItemsStore((s) => s.items);
  const createItem = useItemsStore((s) => s.createItem);
  const updateItem = useItemsStore((s) => s.updateItem);
  const commitments = useCommitmentStore((s) => s.commitments);

  const [step, setStep] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [kept, setKept] = useState<KeptRanking | null>(null);
  const [draft, setDraft] = useState('');
  const [order, setOrder] = useState<string[]>([]);
  const [startAt, setStartAt] = useState(() => toTimeInput(defaultTimeboxStart()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plate = useMemo(() => plateItems(items), [items]);
  const candidates = useMemo(() => rankingCandidates(items), [items]);
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const timebox = userId ? todaysTimebox(commitments, userId) : undefined;
  const topThree = (kept?.rankedItemIds ?? []).slice(0, 3).map((id) => byId.get(id)).filter((i): i is Item => Boolean(i));
  const northStar = kept ? northStarOf(kept.rankedItemIds, items) : undefined;
  const bigAvoidance = (kept?.rankedItemIds ?? []).map((id) => byId.get(id)).find((i) => i && bootTagsOf(i).includes('A'));

  // Today's ranking and timebox, and the plate's items, read back from the record.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const [ranking] = await Promise.all([
        findKeptRanking(WHATS_NEXT_APP_ID, userId, startOfDay()),
        useCommitmentStore.getState().fetchFromSupabase(),
        useItemsStore.getState().fetchFromSupabase(),
      ]);
      if (cancelled) return;
      setKept(ranking);
      if (ranking) setStep(5);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // The ranking step starts from the candidates, marked ones first.
  useEffect(() => {
    if (step !== 3) return;
    setOrder((current) => {
      const ids = candidates.map((item) => item.id);
      const still = current.filter((id) => ids.includes(id));
      return [...still, ...ids.filter((id) => !still.includes(id))];
    });
  }, [step, candidates]);

  const content = STEPS[step - 1];

  const addToPlate = async () => {
    const title = draft.trim();
    if (!title || !userId) return;
    setBusy(true);
    const ok = await createItem({
      title,
      exit_status: 'active',
      tags: [],
      createdBy: userName || userId,
      workspace_id: getCurrentWorkspaceId() ?? undefined,
    } as Parameters<typeof createItem>[0]);
    setBusy(false);
    if (!ok) {
      setError('That could not be added. Try again.');
      return;
    }
    setError(null);
    setDraft('');
  };

  const letGo = (item: Item) => void updateItem(item.id, { exit_status: 'archived' });

  const toggleTag = (item: Item, tag: BootTag) => {
    const on = bootTagsOf(item).includes(tag);
    void updateItem(item.id, { tags: withBootTag(item.tags ?? [], tag, !on) });
  };

  const move = (index: number, by: number) => {
    setOrder((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const keepOrder = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const day = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const result = await rankAlone({
      appId: WHATS_NEXT_APP_ID,
      name: `Morning Boot, ${day}`,
      order,
      userId,
      userName: userName || 'You',
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setKept({ sessionId: result.sessionId, rankedItemIds: order, createdAt: new Date().toISOString() });
    setStep(4);
  };

  const setTimebox = async () => {
    if (!userId || !northStar) return;
    setBusy(true);
    setError(null);
    const start = fromTimeInput(startAt);
    const store = useCommitmentStore.getState();
    const created = await store.createCommitment({
      title: timeboxTitle(northStar.title),
      description: timeboxDescription(start),
      commitmentType: 'internal',
      dueDate: localIsoDate(),
      ownerId: userId,
      ownerName: userName || null,
    });
    if (created) await store.linkItem(created.id, northStar.id);
    setBusy(false);
    if (!created) {
      setError(store.lastCreateError ?? 'The timebox could not be saved.');
      return;
    }
    setStep(5);
  };

  if (!loaded) {
    return (
      <AppShell app={app}>
        <p className="font-body text-sm text-[var(--text-ter)]">Reading today's Morning Boot…</p>
      </AppShell>
    );
  }

  return (
    <AppShell app={app}>
      <ScreenHeading title="Morning Boot" subtitle="Before email and calendar: your plate, your top three, and time for your North Star." />

      <ol className="mt-4 flex flex-wrap gap-2" aria-label="Morning Boot steps">
        {STEPS.map((s) => (
          <li key={s.step}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(s.step)}
              disabled={s.step > 3 && !kept}
              aria-current={s.step === step ? 'step' : undefined}
              className={`rounded-full px-3 py-1 font-mono text-[11px] ${
                s.step === step
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 disabled:opacity-40 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
              }`}
            >
              {s.step}. {STEP_LABELS[s.step - 1]}
            </Button>
          </li>
        ))}
      </ol>

      <AppCard className="mt-4">
        <Eyebrow>Step {content.step} of {STEPS.length}</Eyebrow>
        <h2 className="mt-1 font-heading font-semibold text-lg text-[var(--text-pri)]">{content.title}</h2>
        <p className="mt-1 font-body text-sm text-[var(--text-sec)]">{content.instruction}</p>
        {content.coachRail ? <p className="mt-2 font-body text-xs italic text-[var(--text-ter)]">{content.coachRail}</p> : null}
      </AppCard>

      {error ? <p role="alert" className="mt-3 font-body text-sm text-[var(--coral)]">{error}</p> : null}

      {step === 1 && (
        <div className="mt-4 space-y-3">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void addToPlate();
            }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Something on your plate"
              aria-label="Something on your plate"
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
            />
            <SecondaryButton type="submit" disabled={busy || !draft.trim()}>Add</SecondaryButton>
          </form>
          {plate.length === 0 ? (
            <EmptyState title="Your plate is empty" hint="Write down everything on it, one thing at a time." />
          ) : (
            <ul className="space-y-2">
              {plate.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                  <span className="min-w-0 truncate font-body text-sm text-[var(--text-pri)]">{item.title}</span>
                  <Button variant="ghost" size="sm" onClick={() => letGo(item)} className="shrink-0 font-body text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)]">
                    Let go
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {plate.length > 0 ? <PrimaryButton onClick={() => setStep(2)}>Next: tag</PrimaryButton> : null}
        </div>
      )}

      {step === 2 && (
        <div className="mt-4 space-y-3">
          <ul className="space-y-2">
            {plate.map((item) => {
              const on = bootTagsOf(item);
              return (
                <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-[var(--text-pri)]">{item.title}</span>
                  <span className="flex gap-1.5">
                    {BOOT_TAGS.map((tag) => (
                      <Button
                        key={tag}
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleTag(item, tag)}
                        aria-pressed={on.includes(tag)}
                        title={TAG_LABELS[tag].description}
                        className={`rounded-full px-2.5 py-0.5 font-mono text-[11px] ${
                          on.includes(tag)
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
                        }`}
                      >
                        {TAG_LABELS[tag].label}
                      </Button>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
          {plate.length >= 2 ? (
            <PrimaryButton onClick={() => setStep(3)}>Next: rank</PrimaryButton>
          ) : (
            <p className="font-body text-xs text-[var(--text-ter)]">Add at least two things to your plate to rank them.</p>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="mt-4 space-y-3">
          <p className="font-body text-xs text-[var(--text-ter)]">
            {candidates.length < plate.length ? 'Your marked items, ' : 'Everything on your plate, '}
            most important first. The first three are your top three.
          </p>
          <ol className="space-y-2">
            {order.map((id, index) => (
              <li key={id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                <span className="w-5 font-mono text-xs text-[var(--text-ter)]">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate font-body text-sm text-[var(--text-pri)]">{byId.get(id)?.title ?? id}</span>
                <Button variant="ghost" size="sm" aria-label="Move up" onClick={() => move(index, -1)} disabled={index === 0} className="text-[var(--text-ter)] disabled:opacity-30">
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" aria-label="Move down" onClick={() => move(index, 1)} disabled={index === order.length - 1} className="text-[var(--text-ter)] disabled:opacity-30">
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ol>
          <PrimaryButton onClick={() => void keepOrder()} disabled={busy || order.length < 2}>
            {busy ? 'Saving your ranking…' : 'Keep this order'}
          </PrimaryButton>
        </div>
      )}

      {step === 4 && northStar && (
        <div className="mt-4 space-y-3">
          <AppCard>
            <Eyebrow>Your North Star step</Eyebrow>
            <p className="mt-1 font-heading font-semibold text-[var(--text-pri)]">{northStar.title}</p>
          </AppCard>
          {timebox ? (
            <p className="font-body text-sm text-[var(--text-sec)]">On your commitments for today: {timebox.description}</p>
          ) : (
            <>
              <label className="flex items-center gap-3 font-body text-sm text-[var(--text-sec)]">
                Start at
                <input
                  type="time"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
                />
                for 30 minutes
              </label>
              <PrimaryButton onClick={() => void setTimebox()} disabled={busy}>
                {busy ? 'Saving…' : 'Put it on my commitments'}
              </PrimaryButton>
            </>
          )}
          <a
            href={generateGoogleCalendarLink(buildNorthStarTimeboxEvent(northStar.title, fromTimeInput(startAt)))}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-body text-sm text-primary-600"
          >
            <CalendarPlus className="h-4 w-4" />
            Add it to Google Calendar
          </a>
        </div>
      )}

      {step === 5 && kept && (
        <div className="mt-4 space-y-3">
          <Eyebrow>Today's three</Eyebrow>
          <ol className="space-y-2">
            {topThree.map((item, index) => (
              <li key={item.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                <span className="font-mono text-sm text-[var(--text-ter)]">{index + 1}</span>
                <span className="font-heading font-semibold text-[var(--text-pri)]">{item.title}</span>
              </li>
            ))}
          </ol>
          {bigAvoidance ? (
            <p className="font-body text-sm text-[var(--text-sec)]">Start with your big avoidance: <strong>{bigAvoidance.title}</strong>.</p>
          ) : null}
          <p className="font-body text-sm text-[var(--text-sec)]">
            {timebox ? `North Star timebox: ${timebox.description}` : 'No timebox for your North Star yet today.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {!timebox ? <PrimaryButton onClick={() => setStep(4)}>Timebox your North Star</PrimaryButton> : null}
            <SecondaryButton onClick={() => setStep(1)}>Run Morning Boot again</SecondaryButton>
          </div>
        </div>
      )}
    </AppShell>
  );
}
